<script setup>
import { computed } from 'vue';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';
import {
  FIELD_INPUT_CLASS,
  FIELD_SELECT_CLASS,
  FIELD_TEXTAREA_CLASS,
  ICON_BUTTON_CLASS,
  RATING_MAX_OPTIONS,
} from '../constants';
import {
  MAX_PLACEHOLDER_LENGTH,
  MAX_TEXT_MAX_LENGTH,
  MIN_CHOICE_OPTIONS,
  isChoiceType,
  normalizeVisibility,
  supportsMaxLength,
  supportsNumberBounds,
  supportsPlaceholder,
  supportsScale,
  supportsVisibilitySource,
  visibilityValuesFor,
} from '../helper/surveyFormHelper';
import { useSurveyLabels } from '../composables/useSurveyLabels';

/**
 * Editor of a single question.
 *
 * Never mutates the incoming question: every change is emitted as a patch so
 * the builder composable stays the only owner of the draft.
 */
const props = defineProps({
  question: { type: Object, required: true },
  index: { type: Number, required: true },
  total: { type: Number, required: true },
  questions: { type: Array, default: () => [] },
  errors: { type: Object, default: () => ({}) },
});

const emit = defineEmits([
  'update',
  'changeType',
  'remove',
  'duplicate',
  'moveUp',
  'moveDown',
  'addOption',
  'removeOption',
  'updateOption',
]);

const {
  t,
  questionTypeOptions,
  questionTypeLabel,
  questionTypeIcon,
  validationMessage,
} = useSurveyLabels();

const label = computed({
  get: () => props.question.label,
  set: value => emit('update', { label: value }),
});

const help = computed({
  get: () => props.question.help,
  set: value => emit('update', { help: value }),
});

const required = computed({
  get: () => props.question.required,
  set: value => emit('update', { required: value }),
});

const scaleMax = computed({
  get: () => props.question.scale?.max ?? 5,
  set: value =>
    emit('update', {
      scale: { min: 1, max: Number.parseInt(value, 10) || 5 },
    }),
});

const maxLength = computed({
  get: () => props.question.max_length ?? '',
  set: value =>
    emit('update', { max_length: Number.parseInt(value, 10) || null }),
});

const placeholder = computed({
  get: () => props.question.placeholder ?? '',
  set: value => emit('update', { placeholder: value }),
});

const minimum = computed({
  get: () => props.question.min ?? '',
  set: value => emit('update', { min: value === '' ? null : value }),
});

const maximum = computed({
  get: () => props.question.max ?? '',
  set: value => emit('update', { max: value === '' ? null : value }),
});

const visibility = computed(
  () => normalizeVisibility(props.question.visible_when) || {}
);

const visibilitySources = computed(() =>
  props.questions
    .slice(0, props.index)
    .map((question, sourceIndex) => ({ question, sourceIndex }))
    .filter(({ question }) => supportsVisibilitySource(question?.type))
    .map(({ question, sourceIndex }) => ({
      value: question.key,
      label:
        question.label ||
        t('SURVEYS.EDITOR.QUESTIONS.VISIBILITY_QUESTION', {
          count: sourceIndex + 1,
        }),
      question,
    }))
);

const visibilitySourceKey = computed({
  get: () => visibility.value.question_key || '',
  set: questionKey => {
    if (!questionKey) {
      emit('update', { visible_when: null });
      return;
    }

    const value =
      questionKey === visibility.value.question_key
        ? visibility.value.value || ''
        : '';
    emit('update', {
      visible_when: { question_key: questionKey, value },
    });
  },
});

const visibilitySource = computed(() =>
  visibilitySources.value.find(
    source => source.value === visibilitySourceKey.value
  )
);

const visibilityOptionLabel = option => {
  if (visibilitySource.value?.question?.type !== 'boolean') {
    return option.label;
  }

  return option.value === 'true'
    ? t('SURVEYS.EDITOR.QUESTIONS.VISIBILITY_YES')
    : t('SURVEYS.EDITOR.QUESTIONS.VISIBILITY_NO');
};

const visibilityValueOptions = computed(() =>
  visibilityValuesFor(visibilitySource.value?.question).map(option => ({
    ...option,
    label: visibilityOptionLabel(option),
  }))
);

const visibilityValue = computed({
  get: () => visibility.value.value || '',
  set: value => {
    if (!visibilitySourceKey.value) return;

    emit('update', {
      visible_when: { question_key: visibilitySourceKey.value, value },
    });
  },
});

const showVisibilityConfiguration = computed(
  () => visibilitySources.value.length > 0 || Boolean(visibilitySourceKey.value)
);

const hasChoices = computed(() => isChoiceType(props.question.type));
const hasScale = computed(() => supportsScale(props.question.type));
const hasMaxLength = computed(() => supportsMaxLength(props.question.type));
const hasPlaceholder = computed(() => supportsPlaceholder(props.question.type));
const hasNumberBounds = computed(() =>
  supportsNumberBounds(props.question.type)
);
const isFixedScale = computed(() =>
  ['nps', 'likert'].includes(props.question.type)
);
const canRemoveOption = computed(
  () => (props.question.options?.length || 0) > MIN_CHOICE_OPTIONS
);
const hasError = computed(() => Object.keys(props.errors || {}).length > 0);
const fixedScaleHint = computed(() =>
  props.question.type === 'nps'
    ? t('SURVEYS.EDITOR.QUESTIONS.NPS_HINT')
    : t('SURVEYS.EDITOR.QUESTIONS.LIKERT_HINT')
);
</script>

<template>
  <article
    class="rounded-lais border bg-lais-bg-card shadow-lais-card transition"
    :class="hasError ? 'border-lais-danger/60' : 'border-lais-border-soft'"
  >
    <header
      class="flex flex-wrap items-center justify-between gap-2 border-b border-lais-border-soft px-4 py-3"
    >
      <div class="flex min-w-0 items-center gap-2">
        <span
          class="survey-drag-handle grid size-8 shrink-0 cursor-grab place-items-center rounded-lais text-lais-text-muted transition hover:bg-lais-primary-50 hover:text-lais-primary active:cursor-grabbing"
          :title="t('SURVEYS.EDITOR.QUESTIONS.DRAG')"
          :aria-label="t('SURVEYS.EDITOR.QUESTIONS.DRAG')"
        >
          <span class="i-lucide-grip-vertical size-4" aria-hidden="true" />
        </span>
        <span
          class="grid size-7 shrink-0 place-items-center rounded-full bg-lais-primary-50 text-lais-label-sm font-bold tabular-nums text-lais-primary"
        >
          {{ index + 1 }}
        </span>
        <LaisStatusBadge
          :label="questionTypeLabel(question.type)"
          :icon="questionTypeIcon(question.type)"
          tone="neutral"
          size="sm"
        />
        <LaisStatusBadge
          v-if="question.required"
          :label="t('SURVEYS.EDITOR.QUESTIONS.REQUIRED_BADGE')"
          tone="warning"
          size="sm"
        />
      </div>

      <div class="flex items-center gap-1.5">
        <button
          v-tooltip.top="t('SURVEYS.EDITOR.QUESTIONS.MOVE_UP')"
          type="button"
          :class="ICON_BUTTON_CLASS"
          :aria-label="t('SURVEYS.EDITOR.QUESTIONS.MOVE_UP')"
          :disabled="index === 0"
          @click="emit('moveUp')"
        >
          <span class="i-lucide-arrow-up size-4" aria-hidden="true" />
        </button>
        <button
          v-tooltip.top="t('SURVEYS.EDITOR.QUESTIONS.MOVE_DOWN')"
          type="button"
          :class="ICON_BUTTON_CLASS"
          :aria-label="t('SURVEYS.EDITOR.QUESTIONS.MOVE_DOWN')"
          :disabled="index === total - 1"
          @click="emit('moveDown')"
        >
          <span class="i-lucide-arrow-down size-4" aria-hidden="true" />
        </button>
        <button
          v-tooltip.top="t('SURVEYS.EDITOR.QUESTIONS.DUPLICATE')"
          type="button"
          :class="ICON_BUTTON_CLASS"
          :aria-label="t('SURVEYS.EDITOR.QUESTIONS.DUPLICATE')"
          @click="emit('duplicate')"
        >
          <span class="i-lucide-copy size-4" aria-hidden="true" />
        </button>
        <button
          v-tooltip.top="t('SURVEYS.EDITOR.QUESTIONS.REMOVE')"
          type="button"
          :class="ICON_BUTTON_CLASS"
          class="hover:!border-lais-danger hover:!text-lais-danger"
          :aria-label="t('SURVEYS.EDITOR.QUESTIONS.REMOVE')"
          :disabled="total === 1"
          @click="emit('remove')"
        >
          <span class="i-lucide-trash-2 size-4" aria-hidden="true" />
        </button>
      </div>
    </header>

    <div class="grid gap-4 p-4">
      <div class="grid gap-4 md:grid-cols-[minmax(0,1fr)_220px]">
        <LaisField
          :label="t('SURVEYS.EDITOR.QUESTIONS.LABEL')"
          :error="validationMessage(errors.label)"
          required
        >
          <input
            v-model="label"
            type="text"
            :class="FIELD_INPUT_CLASS"
            :placeholder="t('SURVEYS.EDITOR.QUESTIONS.LABEL_PLACEHOLDER')"
          />
        </LaisField>

        <LaisField
          :label="t('SURVEYS.EDITOR.QUESTIONS.TYPE')"
          :error="validationMessage(errors.type)"
        >
          <select
            :value="question.type"
            :class="FIELD_SELECT_CLASS"
            @change="emit('changeType', $event.target.value)"
          >
            <option
              v-for="option in questionTypeOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </LaisField>
      </div>

      <LaisField
        :label="t('SURVEYS.EDITOR.QUESTIONS.HELP')"
        :helper="t('SURVEYS.EDITOR.QUESTIONS.HELP_HELPER')"
      >
        <textarea
          v-model="help"
          rows="2"
          :class="FIELD_TEXTAREA_CLASS"
          :placeholder="t('SURVEYS.EDITOR.QUESTIONS.HELP_PLACEHOLDER')"
        />
      </LaisField>

      <LaisField
        v-if="hasPlaceholder"
        :label="t('SURVEYS.EDITOR.QUESTIONS.PLACEHOLDER')"
        :helper="t('SURVEYS.EDITOR.QUESTIONS.PLACEHOLDER_HELPER')"
        :error="validationMessage(errors.placeholder)"
      >
        <input
          v-model="placeholder"
          type="text"
          :maxlength="MAX_PLACEHOLDER_LENGTH"
          :class="FIELD_INPUT_CLASS"
          :placeholder="t('SURVEYS.EDITOR.QUESTIONS.PLACEHOLDER_EXAMPLE')"
        />
      </LaisField>

      <div v-if="hasChoices" class="grid gap-2">
        <div class="flex items-center justify-between gap-2">
          <span class="text-[13px] font-semibold text-lais-text-body">
            {{ t('SURVEYS.EDITOR.QUESTIONS.OPTIONS') }}
          </span>
          <button
            type="button"
            class="inline-flex items-center gap-1.5 rounded-lais px-2 py-1 text-[13px] font-semibold text-lais-primary transition hover:bg-lais-primary-50 focus-visible:outline-none focus-visible:shadow-lais-focus"
            @click="emit('addOption')"
          >
            <span class="i-lucide-plus size-3.5" aria-hidden="true" />
            {{ t('SURVEYS.EDITOR.QUESTIONS.ADD_OPTION') }}
          </button>
        </div>

        <div
          v-for="(option, optionIndex) in question.options"
          :key="`${question.key}-option-${optionIndex}`"
          class="flex items-center gap-2"
        >
          <span
            class="grid size-8 shrink-0 place-items-center rounded-full bg-lais-bg-soft text-lais-label-xs font-bold tabular-nums text-lais-text-muted"
          >
            {{ optionIndex + 1 }}
          </span>
          <input
            :value="option.label"
            type="text"
            :class="FIELD_INPUT_CLASS"
            :placeholder="t('SURVEYS.EDITOR.QUESTIONS.OPTION_PLACEHOLDER')"
            @input="emit('updateOption', optionIndex, $event.target.value)"
          />
          <button
            v-tooltip.top="t('SURVEYS.EDITOR.QUESTIONS.REMOVE_OPTION')"
            type="button"
            :class="ICON_BUTTON_CLASS"
            :aria-label="t('SURVEYS.EDITOR.QUESTIONS.REMOVE_OPTION')"
            :disabled="!canRemoveOption"
            @click="emit('removeOption', optionIndex)"
          >
            <span class="i-lucide-x size-4" aria-hidden="true" />
          </button>
        </div>

        <p v-if="errors.options" class="text-[12px] text-lais-danger">
          {{ validationMessage(errors.options) }}
        </p>
      </div>

      <div class="grid gap-4 sm:grid-cols-2">
        <LaisField
          v-if="hasScale"
          :label="t('SURVEYS.EDITOR.QUESTIONS.SCALE_MAX')"
          :helper="t('SURVEYS.EDITOR.QUESTIONS.SCALE_MAX_HELPER')"
          :error="validationMessage(errors.scale)"
        >
          <select v-model="scaleMax" :class="FIELD_SELECT_CLASS">
            <option
              v-for="value in RATING_MAX_OPTIONS"
              :key="value"
              :value="value"
            >
              {{ t('SURVEYS.EDITOR.QUESTIONS.SCALE_OPTION', { count: value }) }}
            </option>
          </select>
        </LaisField>

        <LaisField
          v-if="hasMaxLength"
          :label="t('SURVEYS.EDITOR.QUESTIONS.MAX_LENGTH')"
          :helper="t('SURVEYS.EDITOR.QUESTIONS.MAX_LENGTH_HELPER')"
          :error="validationMessage(errors.max_length)"
        >
          <input
            v-model="maxLength"
            type="number"
            min="1"
            :max="MAX_TEXT_MAX_LENGTH"
            :class="FIELD_INPUT_CLASS"
          />
        </LaisField>

        <LaisField
          v-if="hasNumberBounds"
          :label="t('SURVEYS.EDITOR.QUESTIONS.NUMBER_MIN')"
          :helper="t('SURVEYS.EDITOR.QUESTIONS.NUMBER_MIN_HELPER')"
          :error="validationMessage(errors.number)"
        >
          <input
            v-model="minimum"
            type="number"
            step="any"
            inputmode="decimal"
            :class="FIELD_INPUT_CLASS"
          />
        </LaisField>

        <LaisField
          v-if="hasNumberBounds"
          :label="t('SURVEYS.EDITOR.QUESTIONS.NUMBER_MAX')"
          :helper="t('SURVEYS.EDITOR.QUESTIONS.NUMBER_MAX_HELPER')"
        >
          <input
            v-model="maximum"
            type="number"
            step="any"
            inputmode="decimal"
            :class="FIELD_INPUT_CLASS"
          />
        </LaisField>

        <label
          class="flex items-center gap-2.5 self-end rounded-lais border border-lais-border bg-white px-3 py-2.5 text-[13px] font-semibold text-lais-text"
        >
          <input
            v-model="required"
            type="checkbox"
            class="size-4 rounded border-lais-border text-lais-primary focus-visible:outline-none focus-visible:shadow-lais-focus"
          />
          {{ t('SURVEYS.EDITOR.QUESTIONS.REQUIRED') }}
        </label>
      </div>

      <section
        v-if="showVisibilityConfiguration"
        class="grid gap-3 rounded-lais border border-lais-border-soft bg-lais-bg-soft/50 p-3 sm:grid-cols-2"
      >
        <div class="flex items-start gap-2 sm:col-span-2">
          <span
            class="i-lucide-git-branch mt-0.5 size-4 shrink-0 text-lais-primary"
            aria-hidden="true"
          />
          <div class="min-w-0">
            <p class="text-[13px] font-semibold text-lais-text">
              {{ t('SURVEYS.EDITOR.QUESTIONS.VISIBILITY_TITLE') }}
            </p>
            <p class="mt-0.5 text-[12px] leading-snug text-lais-text-muted">
              {{ t('SURVEYS.EDITOR.QUESTIONS.VISIBILITY_HELPER') }}
            </p>
          </div>
        </div>

        <LaisField
          :label="t('SURVEYS.EDITOR.QUESTIONS.VISIBILITY_SOURCE')"
          :error="validationMessage(errors.visible_when)"
        >
          <select v-model="visibilitySourceKey" :class="FIELD_SELECT_CLASS">
            <option value="">
              {{ t('SURVEYS.EDITOR.QUESTIONS.VISIBILITY_ALWAYS') }}
            </option>
            <option
              v-for="source in visibilitySources"
              :key="source.value"
              :value="source.value"
            >
              {{ source.label }}
            </option>
          </select>
        </LaisField>

        <LaisField
          v-if="visibilitySourceKey"
          :label="t('SURVEYS.EDITOR.QUESTIONS.VISIBILITY_VALUE')"
        >
          <select v-model="visibilityValue" :class="FIELD_SELECT_CLASS">
            <option value="" disabled>
              {{ t('SURVEYS.EDITOR.QUESTIONS.VISIBILITY_VALUE_PLACEHOLDER') }}
            </option>
            <option
              v-for="option in visibilityValueOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label || option.value }}
            </option>
          </select>
        </LaisField>
      </section>

      <p
        v-if="isFixedScale"
        class="flex items-start gap-2 rounded-lais bg-lais-bg-soft px-3 py-2 text-[12px] text-lais-text-body"
      >
        <span
          class="i-lucide-info mt-0.5 size-3.5 shrink-0"
          aria-hidden="true"
        />
        {{ fixedScaleHint }}
      </p>
    </div>
  </article>
</template>
